<template>
  <section class="page-shell my-account-page">
    <div class="page-heading">
      <div>
        <h1>Minha conta</h1>
        <p>Dados pessoais e profissionais usados para preencher documentos automaticamente.</p>
      </div>
    </div>

    <section class="data-panel my-account-panel">
      <div class="panel-title-row">
        <div>
          <h2>Perfil profissional</h2>
          <p>Essas informações entram nas minutas quando a opção de preenchimento automático estiver ativa.</p>
        </div>
        <v-chip small color="primary" text-color="white">Uso pessoal</v-chip>
      </div>

      <v-alert v-if="message" type="success" dense text>{{ message }}</v-alert>
      <v-alert v-if="error" type="error" dense text>{{ error }}</v-alert>

      <div class="form-grid">
        <v-text-field
          v-model="form.name"
          label="Nome de exibição"
          outlined
          dense
          hide-details
          @blur="form.name = formatName(form.name)"
        />
        <v-text-field
          v-model="form.professional_full_name"
          label="Nome completo profissional"
          outlined
          dense
          hide-details
          @blur="form.professional_full_name = formatName(form.professional_full_name)"
        />
      </div>

      <div class="form-grid">
        <v-text-field v-model="form.oab_number" label="Número da OAB" outlined dense hide-details />
        <v-text-field
          v-model="form.oab_section"
          label="Seccional"
          outlined
          dense
          hide-details
          placeholder="SP, RS, RJ..."
          @input="form.oab_section = formatState(form.oab_section)"
        />
      </div>

        <v-text-field
          v-model="form.professional_title"
          label="Título/cargo"
          outlined
          dense
          hide-details
          placeholder="Advogado responsável, Sócio, Coordenador jurídico..."
          @blur="form.professional_title = formatName(form.professional_title)"
        />

      <div class="profile-preview">
        <span>Prévia para documentos</span>
        <strong>{{ previewName }}</strong>
        <p>{{ previewOab }}</p>
      </div>

      <div class="panel-actions">
        <v-btn color="primary" :loading="saving" :disabled="!hasChanges" @click="save">
          Salvar alterações
        </v-btn>
      </div>
    </section>
  </section>
</template>

<script>
const emptyForm = () => ({
  name: '',
  professional_full_name: '',
  oab_number: '',
  oab_section: '',
  professional_title: ''
})

export default {
  name: 'MyAccountPage',
  data () {
    return {
      form: emptyForm(),
      original: emptyForm(),
      saving: false,
      message: '',
      error: ''
    }
  },
  computed: {
    hasChanges () {
      return JSON.stringify(this.form) !== JSON.stringify(this.original)
    },
    previewName () {
      return this.form.professional_full_name || this.form.name || 'Nome profissional não informado'
    },
    previewOab () {
      const number = String(this.form.oab_number || '').trim()
      const section = String(this.form.oab_section || '').trim().toUpperCase()
      const title = this.form.professional_title || 'Advogado responsável'

      if (!number && !section) return `${title} · OAB não informada`
      return `${title} · OAB/${section || 'UF'} ${number || 'número'}`
    }
  },
  created () {
    this.load()
  },
  methods: {
    load () {
      const user = this.$store.state.auth.user || {}
      this.form = {
        name: user.name || '',
        professional_full_name: user.professional_full_name || '',
        oab_number: user.oab_number || '',
        oab_section: user.oab_section || '',
        professional_title: user.professional_title || ''
      }
      this.original = { ...this.form }
    },
    async save () {
      this.saving = true
      this.error = ''
      this.message = ''

      try {
        this.normalizeForm()
        await this.$store.dispatch('auth/updateMe', {
          ...this.form,
          oab_section: this.formatState(this.form.oab_section)
        })
        this.load()
        this.message = 'Perfil profissional atualizado.'
      } catch (error) {
        this.error = error.response && error.response.data ? error.response.data.message : 'Não foi possível salvar seu perfil.'
      } finally {
        this.saving = false
      }
    },
    normalizeForm () {
      this.form.name = this.formatName(this.form.name)
      this.form.professional_full_name = this.formatName(this.form.professional_full_name)
      this.form.professional_title = this.formatName(this.form.professional_title)
      this.form.oab_section = this.formatState(this.form.oab_section)
    },
    formatState (value) {
      return String(value || '').replace(/[^a-zA-Z]/g, '').slice(0, 2).toUpperCase()
    },
    formatName (value) {
      return String(value || '')
        .trim()
        .replace(/\s+/g, ' ')
        .toLowerCase()
        .replace(/(^|\s)([a-záàâãéèêíïóôõöúçñ])/g, (match, space, letter) => `${space}${letter.toUpperCase()}`)
    }
  }
}
</script>

<style lang="scss" scoped>
.my-account-page {
  max-width: 980px;
}

.my-account-panel {
  display: grid;
  gap: 18px;
}

.panel-title-row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
  padding-bottom: 16px;
  border-bottom: 1px solid #e2e8e5;
}

.panel-title-row h2 {
  margin: 0 0 5px;
  color: #101918;
}

.panel-title-row p {
  margin: 0;
  color: #667a75;
}

.profile-preview {
  padding: 16px;
  border: 1px solid #dfe8e4;
  background: #f8fbfa;
}

.profile-preview span,
.profile-preview strong,
.profile-preview p {
  display: block;
}

.profile-preview span {
  margin-bottom: 6px;
  color: #0f4efb;
  font-size: 11px;
  font-weight: 850;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.profile-preview strong {
  color: #101918;
  font-size: 18px;
}

.profile-preview p {
  margin: 4px 0 0;
  color: #536963;
}

.panel-actions {
  display: flex;
  justify-content: flex-end;
}

@media (max-width: 760px) {
  .panel-title-row,
  .panel-actions {
    display: grid;
  }
}
</style>
